import React, { useState, useEffect } from 'react';
import AdsCard from '../AdsCard';
import spotify from '../../../../assests/spotify.svg';
import { toast } from 'react-toastify';
import { fetchCredentials, fetchAuthCodeFromSpotify, getSpotifyAdAccountList } from '../../../../services/ApiService';
import { getOAuthRedirectUri } from '../../../../utils/oauthRedirect';
import { getIntegrationErrorMessage } from '../../../../utils/oauthErrors';
import { CommonModal } from '../../../CommonModal';
import { SpotifyAdsAccountTable } from './SpotifyAdsAccountsTable';

const spotifyOAuthDevLog = (...args) => {
  if (process.env.NODE_ENV === 'development') {
    console.log('[Spotify OAuth Dev]', ...args);
  }
};

const SpotifyAdsIntegration = ({ isAccountLinked, fetchAdsAccountStatus, accountDetails, social_media_type }) => {
  const [connecting, setConnecting] = useState(false);
  const [spotifyAdAccounts, setSpotifyAdAccounts] = useState([]);
  const [showSpotifyAdAccounts, setShowSpotifyAdAccounts] = useState(false);

  const handleConnect = async () => {
    setConnecting(true);
    localStorage.setItem('selected_platform_to_connect', 'spotify');
    localStorage.removeItem('spotify_code');

    const redirectUri = getOAuthRedirectUri();
    spotifyOAuthDevLog('Connect clicked', { redirect_uri: redirectUri });

    if (!redirectUri) {
      setConnecting(false);
      toast.error('Spotify redirect URL is not configured.');
      return;
    }

    const credentials = await fetchCredentials();

    if (credentials?.spotify?.spotify_ads_client_id) {
      fetchAuthCodeFromSpotify(credentials.spotify.spotify_ads_client_id, redirectUri);
    } else {
      setConnecting(false);
      toast.error('Error connecting to Spotify');
    }
  };

  const connectToAccount = async (code) => {
    setConnecting(true);
    try {
      const result = await getSpotifyAdAccountList(code);

      if (result?.success) {
        const accounts = Array.isArray(result?.data?.ad_accounts_list)
          ? result.data.ad_accounts_list
          : [];

        if (accounts.length > 0) {
          setSpotifyAdAccounts(accounts);
          setShowSpotifyAdAccounts(true);
        } else {
          toast.error('No Spotify ad accounts found for this account.');
        }
      } else {
        toast.error(getIntegrationErrorMessage(result, 'Failed to connect to Spotify.'));
      }
    } catch (e) {
      console.error('[Spotify OAuth Dev] Connect failed', e);
      toast.error(getIntegrationErrorMessage(e, 'Failed to connect to Spotify.'));
    } finally {
      setConnecting(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const error = params.get('error');
    const errorDescription = params.get('error_description');
    const selectedPlatform = localStorage.getItem('selected_platform_to_connect');

    if (error && selectedPlatform === 'spotify') {
      toast.error(
        errorDescription ||
          `Spotify authorization failed. Add this redirect URL in Spotify Developer Dashboard: ${getOAuthRedirectUri()}`,
      );
      setConnecting(false);
      return;
    }

    if (code && selectedPlatform === 'spotify') {
      connectToAccount(code);
    }
  }, []);

  return (
    <>
      <AdsCard
        handleConnect={handleConnect}
        isAccountLinked={isAccountLinked || false}
        clientData={{}}
        connecting={connecting}
        platform="Spotify Ads"
        platform_icon={spotify}
        description="Spotify Ads is a digital advertising service that allows brands to promote their products and services through audio, video, and display ads."
        accountDetails={accountDetails}
        social_media_type={social_media_type}
        fetchAdsAccountStatus={fetchAdsAccountStatus}
      />

      <CommonModal
        show={showSpotifyAdAccounts}
        setShow={setShowSpotifyAdAccounts}
        title={
          <>
            <div className="d-flex flex-column gap-1">
              <span className="fs-4 fw-bold">Your Spotify Ads Accounts</span>
              <small className="fs-8">Select an account to proceed with integration.</small>
            </div>
          </>
        }
        body={
          <SpotifyAdsAccountTable
            fetchAdsAccountStatus={fetchAdsAccountStatus}
            setShowAdAccountsModal={setShowSpotifyAdAccounts}
            adAccounts={spotifyAdAccounts}
            setAdAccounts={setSpotifyAdAccounts}
          />
        }
      />
    </>
  );
};

export default SpotifyAdsIntegration;
